import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { useSeason } from '../contexts/SeasonContext';
import { SeasonSelector } from '../components/common/SeasonSelector';
import { BarChart3, ChevronRight } from 'lucide-react';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

export default function StandingsPage() {
    const [standings, setStandings] = useState([]);
    const [loading, setLoading] = useState(true);
    const { currentSeason } = useSeason();
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentSeason) return;
        setLoading(true);
        API.get(`/standings?season=${encodeURIComponent(currentSeason)}`).then(res => {
            setStandings(res.data.standings || []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [currentSeason]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="standings-page">
            <div className="glass-card p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <BarChart3 className="w-5 h-5 text-yellow-400" />
                        <h2 className="heading-primary text-base md:text-xl text-white">IPL POINTS TABLE</h2>
                    </div>
                    <SeasonSelector />
                </div>
            </div>

            {/* Pre-season indicator */}
            {standings?.length > 0 && standings[0]?.pre_season && (
                <div className="glass-card p-4 border border-yellow-500/20 text-center" data-testid="pre-season-banner">
                    <div className="text-xs text-yellow-400 tech-mono">SEASON NOT STARTED — STANDINGS WILL UPDATE AFTER MATCHES BEGIN</div>
                    <div className="text-[10px] text-white/20 mt-1">All teams start with 0 points. Live standings will sync automatically.</div>
                </div>
            )}

            <div className="glass-card overflow-hidden overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow className="border-b border-white/5 hover:bg-transparent">
                            <TableHead className="tech-mono text-[10px] text-white/40 w-12">#</TableHead>
                            <TableHead className="tech-mono text-[10px] text-white/40">TEAM</TableHead>
                            <TableHead className="tech-mono text-[10px] text-white/40 text-center">P</TableHead>
                            <TableHead className="tech-mono text-[10px] text-white/40 text-center">W</TableHead>
                            <TableHead className="tech-mono text-[10px] text-white/40 text-center">L</TableHead>
                            <TableHead className="tech-mono text-[10px] text-white/40 text-center">PTS</TableHead>
                            <TableHead className="tech-mono text-[10px] text-white/40 text-center">NRR</TableHead>
                            <TableHead className="tech-mono text-[10px] text-white/40 text-center">WIN %</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {standings.map((team, i) => (
                            <TableRow key={team.team_id}
                                className={`border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${i < 4 ? 'bg-green-500/5' : ''}`}
                                onClick={() => navigate(`/teams?team=${team.team_id}`)}
                                data-testid={`standings-row-${team.team_id}`}>
                                <TableCell>
                                    <span className={`heading-primary text-lg ${i < 4 ? 'text-green-400' : 'text-white/40'}`}>{team.position}</span>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-black" style={{ backgroundColor: `${team.color}20`, color: team.color }}>
                                            {team.short_name?.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="heading-primary text-sm text-white">{team.short_name}</div>
                                            <div className="text-[10px] text-white/30">{team.team_name}</div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className="text-center text-white/80 font-bold">{team.played ?? team.matches_played}</TableCell>
                                <TableCell className="text-center text-green-400 font-bold">{team.won ?? team.matches_won}</TableCell>
                                <TableCell className="text-center text-red-400 font-bold">{team.lost ?? team.matches_lost}</TableCell>
                                <TableCell className="text-center">
                                    <span className="heading-primary text-lg text-yellow-400">{team.points}</span>
                                </TableCell>
                                <TableCell className="text-center">
                                    <span className={`font-bold ${team.nrr > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {team.nrr > 0 ? '+' : ''}{team.nrr.toFixed(3)}
                                    </span>
                                </TableCell>
                                <TableCell className="text-center">
                                    <div className="flex items-center justify-center gap-2">
                                        <span className="text-white font-bold">{(team.win_rate * 100).toFixed(0)}%</span>
                                        <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                            <div className="h-full rounded-full" style={{ width: `${team.win_rate * 100}%`, backgroundColor: team.color }} />
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center gap-4 text-xs text-white/30">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm bg-green-500/20 border border-green-500/30" />
                    <span>Playoff qualification zone (Top 4)</span>
                </div>
            </div>
        </div>
    );
}
